import * as React from 'react';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from "../ui/card";
import { Copy, ChevronDown, ChevronUp, Eye, ExternalLink, Trash2 } from 'lucide-react';
import { Product, formatPublishDate } from '../../types/product';
import { optimizationService } from '../../services/optimization/OptimizationService';
import { productService } from '../../services/ProductService';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import ProductPreviewDialog from './ProductPreviewDialog';
import OptimizeDialog from './OptimizeDialog';
import ImagePreviewDialog from './ImagePreviewDialog';
import { PublishToShopifyButton } from '../PublishToShopifyButton';
import { formatStoreName } from '../../utils/formatters';
import { useToast } from '../ui/use-toast';
import { ProductVersionHistory } from './ProductVersionHistory';

interface ProductDetailProps {
  product?: Product;
  onRefresh?: () => void;
}

interface ContentSectionProps {
  title: string;
  original: string;
  optimized?: string;
  isActive: boolean;
  onToggle: () => void;
  productId: string;
  marketplace: 'Shopify' | 'Google Shopping' | 'Amazon';
  field: 'title' | 'description' | 'bulletPoints' | 'metaDescription' | 'productType' | 'productCategory';
  onUpdate: () => void;
}

const ExternalLinkButton = ({ href, children }: { href: string; children: React.ReactNode }) => {
  if (!href) return null;
  
  // Extract the URL from the HYPERLINK formula
  const urlMatch = href.match(/=HYPERLINK\("([^"]+)"/);
  const url = urlMatch ? urlMatch[1] : href;
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
    >
      {children}
      <ExternalLink className="h-4 w-4" />
    </a>
  );
};

const ContentSection = ({ 
  title, 
  original, 
  optimized, 
  isActive,
  onToggle,
  productId,
  marketplace,
  field,
  onUpdate
}: ContentSectionProps) => {
  const [copiedOriginal, setCopiedOriginal] = useState(false);
  const [copiedOptimized, setCopiedOptimized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(optimized || '');
  const toast = useToast();

  useEffect(() => {
    setEditedContent(optimized || '');
  }, [optimized]);

  const handleSave = async () => {
    try {
      // For bullet points, split the content into an array
      const value = field === 'bulletPoints' 
        ? editedContent.split('\n').filter(Boolean)
        : editedContent;

      await optimizationService.updateOptimizedContent(
        productId,
        marketplace,
        field,
        value
      );

      setIsEditing(false);
      onUpdate();
      toast.toast({
        description: "Changes saved successfully",
        duration: 2000,
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.toast({
        description: "Failed to save changes",
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  const handleCancel = () => {
    setEditedContent(optimized || '');
    setIsEditing(false);
  };

  const copyToClipboard = async (text: string, isOriginal: boolean) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.toast({
        description: "Copied to clipboard",
        duration: 2000,
      });
      if (isOriginal) {
        setCopiedOriginal(true);
        setTimeout(() => setCopiedOriginal(false), 2000);
      } else {
        setCopiedOptimized(true);
        setTimeout(() => setCopiedOptimized(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.toast({
        description: "Failed to copy to clipboard",
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-t-lg border-b"
      >
        <span className="font-medium text-lg">{title}</span>
        {isActive ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>
      
      {isActive && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 p-4 md:p-6 bg-white rounded-b-lg">
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-500">Original</h4>
              <button
                onClick={() => copyToClipboard(original, true)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 relative"
              >
                <Copy className="h-4 w-4" />
                {copiedOriginal && (
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded animate-fade-in-out">
                    Copied!
                  </span>
                )}
              </button>
            </div>
            <div className="p-4 bg-gray-50 rounded min-h-[150px] whitespace-pre-wrap">
              {original}
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-500">Optimized</h4>
              <div className="flex items-center gap-2">
                {optimized && !isEditing && (
                  <>
                    <button
                      onClick={() => copyToClipboard(optimized, false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 relative"
                    >
                      <Copy className="h-4 w-4" />
                      {copiedOptimized && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                  </>
                )}
                {isEditing && (
                  <>
                    <button
                      onClick={handleSave}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded min-h-[150px]">
              {isEditing ? (
                <textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="w-full h-full min-h-[150px] p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter optimized content..."
                />
              ) : (
                <div className="whitespace-pre-wrap">
                  {optimized || (
                    <span className="text-gray-400 italic">Not yet optimized</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductDetailView = React.forwardRef<HTMLDivElement, ProductDetailProps>(
  ({ product, onRefresh }, ref) => {
    const [activeSection, setActiveSection] = useState<string>('title');
    const [activeMarketplace, setActiveMarketplace] = useState<'Shopify' | 'Google Shopping' | 'Amazon'>('Shopify');
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
    const [showReoptimizeConfirm, setShowReoptimizeConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const toast = useToast();

    if (!product) {
      return <div>No product selected</div>;
    }

    const getOptimizedContent = () => {
      const marketplaceKey = activeMarketplace.toLowerCase().replace(' ', '') as keyof typeof product.optimizations;
      return product.optimizations[marketplaceKey];
    };

    const optimizedContent = getOptimizedContent();

    const handleOptimizeComplete = async () => {
      setShowOptimizeDialog(false);
      if (onRefresh) {
        await onRefresh();
      }
    };

    const getImageUrl = (image: Product['images'][0]) => {
      return image.thumbnailUrl || image.url;
    };

    const isMarketplaceOptimized = (marketplace: 'Shopify' | 'Google Shopping' | 'Amazon') => {
      const marketplaceKey = marketplace.toLowerCase().replace(' ', '') as keyof typeof product.optimizations;
      return !!product.optimizations[marketplaceKey]?.title;
    };

    const hasOptimizedContent = () => {
      const marketplaceKey = activeMarketplace.toLowerCase().replace(' ', '') as keyof typeof product.optimizations;
      const content = product.optimizations[marketplaceKey];
      return Boolean(
        content?.title || 
        content?.description || 
        (Array.isArray(content?.bulletPoints) && content?.bulletPoints && content.bulletPoints.length > 0)
      );
    };

    const getSections = () => {
      const optimizedContent = getOptimizedContent();
      const baseSections = [
        {
          id: 'title',
          title: 'Title',
          original: product.basicInfo.title,
          optimized: optimizedContent?.title
        },
        {
          id: 'description',
          title: 'Description',
          original: product.basicInfo.description,
          optimized: optimizedContent?.description
        },
        {
          id: 'bulletPoints',
          title: 'Bullet Points',
          original: product.basicInfo.bulletPoints.join('\n'),
          optimized: optimizedContent?.bulletPoints?.join('\n')
        },
        {
          id: 'metaDescription',
          title: 'Meta Description',
          original: product.basicInfo.metaDescription || 'No meta description available',
          optimized: optimizedContent?.metaDescription
        },
        {
          id: 'pageTitle',
          title: 'Page Title',
          original: product.basicInfo.pageTitle || 'No page title available',
          optimized: optimizedContent?.pageTitle
        }
      ];

      if (activeMarketplace === 'Google Shopping') {
        return [
          ...baseSections,
          {
            id: 'category',
            title: 'Product Category',
            original: product.basicInfo.categories.join(', '),
            optimized: optimizedContent?.productCategory
          },
          {
            id: 'type',
            title: 'Product Type',
            original: '',
            optimized: optimizedContent?.productType
          }
        ];
      }

      return baseSections;
    };

    const extractProductId = (url: string | undefined) => {
      if (!url) return '';
      const match = url.match(/\/products\/([^/?]+)/);
      return match ? match[1] : '';
    };

    const handleMarketplaceClick = (marketplace: 'Shopify' | 'Google Shopping' | 'Amazon') => {
      if (isMarketplaceOptimized(marketplace)) {
        setActiveMarketplace(marketplace);
        setShowReoptimizeConfirm(true);
      } else {
        setActiveMarketplace(marketplace);
        setShowOptimizeDialog(true);
      }
    };

    const handleReoptimizeConfirm = () => {
      setShowReoptimizeConfirm(false);
      setShowOptimizeDialog(true);
    };

    return (
      <>
        <Card className="h-full overflow-auto" ref={ref}>
          <CardHeader className="border-b">
            <div className="space-y-4">
              {/* Product Header */}
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex flex-col md:flex-row items-start gap-4 w-full md:w-auto">
                  {/* Image Gallery */}
                  <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                    {product.images.slice(0, 5).map((image, index) => (
                      <div 
                        key={index} 
                        className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setShowImagePreview(true);
                        }}
                      >
                        <img 
                          src={getImageUrl(image)}
                          alt={`${product.basicInfo.title} #${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>

                    <div style={{ maxWidth: '75%' }}>
                      <h2 className="text-2xl font-semibold">{product.basicInfo.title || 'Untitled Product'}</h2>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>{formatStoreName(product.storeName)}</span>
                        {product.basicInfo.sku && (
                          <>
                            <span>•</span>
                            <span>{product.basicInfo.sku}</span>
                          </>
                        )}
                      </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {product.links.shopify && (
                    <ExternalLinkButton href={product.links.shopify}>
                      Shopify
                    </ExternalLinkButton>
                  )}
                  {product.links.amazon && (
                    <ExternalLinkButton href={product.links.amazon}>
                      Amazon
                    </ExternalLinkButton>
                  )}
                  {hasOptimizedContent() && (
                    <button
                      onClick={() => setIsPreviewOpen(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors"
                    >
                      <Eye className="h-4 w-4" />
                      Preview
                    </button>
                  )}
                </div>
              </div>

              {/* Marketplace Selection */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex flex-wrap gap-2">
                  {(['Shopify', 'Google Shopping', 'Amazon'] as const).map((marketplace) => {
                    const isOptimized = isMarketplaceOptimized(marketplace);
                    return (
                      <button
                        key={marketplace}
                        onClick={() => handleMarketplaceClick(marketplace)}
                        className={`px-3 py-1 text-sm rounded-full transition-colors ${
                          activeMarketplace === marketplace
                            ? isOptimized 
                              ? 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100'
                              : 'bg-red-50 text-red-800 border-red-200 hover:bg-red-100'
                            : isOptimized
                              ? 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200'
                              : 'bg-red-50 text-red-600 hover:bg-red-100 border-red-200'
                        }`}
                        title={isOptimized ? 'Optimized' : 'Click to optimize'}
                      >
                        {marketplace}
                      </button>
                    );
                  })}
                </div>
                {activeMarketplace === 'Shopify' && 
                 product.storeName && 
                 product.links.shopify && 
                 optimizedContent?.title && 
                 optimizedContent?.description && (
                  <div className="w-full md:w-auto md:ml-auto flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                    <span className="text-sm text-gray-500">
                      {product.publishTimestamps?.shopify 
                        ? `Published: ${formatPublishDate(product.publishTimestamps.shopify)}`
                        : 'Not yet published'}
                    </span>
                    <PublishToShopifyButton
                      storeName={formatStoreName(product.storeName)}
                      productId={extractProductId(product.links.shopify)}
                      documentId={product.id}
                      title={optimizedContent.title}
                      description={optimizedContent.description}
                      bulletPoints={optimizedContent.bulletPoints || []}
                      pageTitle={optimizedContent.pageTitle}
                      metaDescription={optimizedContent.metaDescription}
                      onPublishComplete={onRefresh}
                    />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            <ProductVersionHistory
              productId={product.id}
              onVersionRestored={onRefresh}
            />
            {getSections().map((section) => (
              <ContentSection
                key={section.id}
                title={section.title}
                original={section.original}
                optimized={section.optimized}
                isActive={activeSection === section.id}
                onToggle={() => setActiveSection(activeSection === section.id ? '' : section.id)}
                productId={product.id}
                marketplace={activeMarketplace}
                field={section.id as 'title' | 'description' | 'bulletPoints' | 'metaDescription' | 'productType' | 'productCategory'}
                onUpdate={onRefresh || (() => {})}
              />
            ))}

            {/* Delete Button Section */}
            <div className="mt-8 flex justify-end border-t pt-6">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete Product
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        {isPreviewOpen && product && (
          <ProductPreviewDialog
            isOpen={isPreviewOpen}
            onClose={() => setIsPreviewOpen(false)}
            product={product}
            optimizedContent={optimizedContent}
            marketplace={activeMarketplace}
            lastOptimized={optimizedContent?.lastOptimized
              ? ('toDate' in optimizedContent.lastOptimized
                  ? optimizedContent.lastOptimized.toDate().toISOString()
                  : optimizedContent.lastOptimized.toISOString())
              : 'Not yet optimized'}
          />
        )}

        {/* Optimize Dialog */}
        {showOptimizeDialog && product && (
          <OptimizeDialog
            isOpen={showOptimizeDialog}
            onClose={() => setShowOptimizeDialog(false)}
            product={product}
            marketplace={activeMarketplace}
            onOptimizeComplete={handleOptimizeComplete}
          />
        )}

        {/* Reoptimize Confirm Dialog */}
        {showReoptimizeConfirm && (
          <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${showReoptimizeConfirm ? '' : 'hidden'}`}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-lg font-semibold mb-4">Confirm Re-optimization</h2>
              <p className="text-gray-600 mb-6">
                This product has been optimized for {activeMarketplace}. Are you sure you want to run optimization again?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowReoptimizeConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  No Thanks
                </button>
                <button
                  onClick={handleReoptimizeConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Yes, Please
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Dialog */}
        <ImagePreviewDialog
          isOpen={showImagePreview}
          onClose={() => setShowImagePreview(false)}
          images={product?.images || []}
          selectedImageIndex={selectedImageIndex}
        />
        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={async () => {
            try {
              await productService.deleteProduct(product.id);
              if (onRefresh) {
                await onRefresh();
              }
              toast.toast({
                description: "Product deleted successfully",
                duration: 2000,
              });
            } catch (error) {
              console.error('Error deleting product:', error);
              toast.toast({
                description: "Failed to delete product",
                variant: "destructive",
                duration: 2000,
              });
            }
          }}
          title="Delete Product"
          description="Are you sure you want to delete this product? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="destructive"
        />
      </>
    );
  }
);

ProductDetailView.displayName = "ProductDetailView";

export { ProductDetailView };